import polyline from "@mapbox/polyline";

type LatLng = { lat: number; lng: number };

// Statuses where the web receipt draws the driver's own route instead of the trip
// estimate: to the pickup while approaching, to the drop-off once arrived or under way.
// A scheduled ride shows the driver's taxi only then too: before it (ACCEPTED, CHECK_IN)
// the rider is told the driver will share their live location nearer the pickup time.
export const DRIVER_ROUTE_STATUSES = ["APPROACHING", "ARRIVED", "ON_GOING"];

const samePoint = (a: [number, number] | undefined, b: LatLng) =>
  a !== undefined && a[0] === b.lat && a[1] === b.lng;

/**
 * The driver's route as the map's encoded polyline, matching the web receipt: the
 * receipt API's `driver.shape` (precision 6), starting at the driver and ending at
 * the pickup (APPROACHING) or the drop-off (ARRIVED, ON_GOING). Undefined when there
 * is no shape or driver position, so the caller keeps the trip estimate.
 */
export const driverRoutePolyline = (args: {
  status: string;
  shape: string | null | undefined;
  driverPosition: LatLng | null | undefined;
  pickup: LatLng | null;
  dropoff: LatLng | null;
}): string | undefined => {
  const { status, shape, driverPosition } = args;
  const target = status === "APPROACHING" ? args.pickup : args.dropoff;
  if (!DRIVER_ROUTE_STATUSES.includes(status) || !shape || !driverPosition || !target) {
    return undefined;
  }

  let path: [number, number][];
  try {
    // Precision 6, decoded as the web receipt does: default precision, then x0.1.
    path = polyline
      .decode(shape)
      .map(([lat, lng]): [number, number] => [lat * 0.1, lng * 0.1]);
  } catch {
    return undefined;
  }
  if (!samePoint(path[0], driverPosition)) {
    path.unshift([driverPosition.lat, driverPosition.lng]);
  }
  if (!samePoint(path[path.length - 1], target)) {
    path.push([target.lat, target.lng]);
  }
  return polyline.encode(path);
};

// Compass bearing (0 = north, clockwise) from one point to another.
const bearing = (from: LatLng, to: LatLng): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const lat1 = toRad(from.lat);
  const lat2 = toRad(to.lat);
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/**
 * Which way the taxi faces, as the web receipt turns it: along the route segment that
 * starts nearest the driver, skipping segments too short to give a direction. Only for
 * the driver's own route (it starts at the driver); 0 (north) otherwise.
 */
export const driverHeading = (path: LatLng[], driver: LatLng): number => {
  const MIN_SEGMENT = 0.00009; // ~10 m
  const first = path[0];
  // The map's polyline keeps 5 decimals, so "starts at the driver" means within ~10 m.
  if (
    path.length < 2 ||
    !first ||
    Math.hypot(first.lat - driver.lat, first.lng - driver.lng) > MIN_SEGMENT
  ) {
    return 0;
  }
  let best: [LatLng, LatLng] | null = null;
  let bestDistance = Infinity;
  for (let i = 0; i < path.length - 1; i++) {
    const from = path[i]!;
    const to = path[i + 1]!;
    if (Math.hypot(to.lat - from.lat, to.lng - from.lng) < MIN_SEGMENT) continue;
    const distance = Math.hypot(driver.lat - from.lat, driver.lng - from.lng);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = [from, to];
    }
  }
  return best ? bearing(best[0], best[1]) : 0;
};
